import { PopoverPlacement } from "@idui/react-popover";
import { AddCircleOutlined } from "@mui/icons-material";
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  IconButton,
  TextField,
} from "@mui/material";
import { FC, SyntheticEvent } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { SVG_MUI } from "@ryde/assets";
import { AccessControl, FramerTooltip } from "@ryde/controls";
import { useDynamicFormsTranslate } from "@ryde/redux";
import {
  checkCleanupNestedValidation,
  ICustomField,
  IDropdownItem,
  Log,
} from "@ryde/shared";

import { StationDropdownBoundTemplateWrapper } from "./station-dropdown-bound.template.styled";
import { useStationDropdownTemplate } from "./use-station-dropdown-template";

interface IStationDropdownBoundProps extends ICustomField {
  onSelect?: (value?: IDropdownItem) => void;
  size?: "small" | "medium";
}

export const StationDropdownBoundTemplate: FC<IStationDropdownBoundProps> = ({
  id,
  name,
  formLabel,
  formAttributes,
  formValidation,
  onSelect,
  e2eData = "station-dropdown-bound-template",
  size = "medium",
}) => {
  const {
    isAddButtonVisible,
    requiredComputed,
    isDisabled,
    isDisabledByMissingAncestor,
    disabledFormLabelTranslated,
    dropdownOptions,
    getOptionLabel,
    dropdownItems,
    handleInsertItem,
    handleFindNearbyStation,
    calculatedWidth,
  } = useStationDropdownTemplate({
    name,
    formAttributes,
    formValidation,
  });

  const { t } = useTranslation();

  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext();

  const { dropItemsSource } = formAttributes || {};
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
  });

  const onChangeDropdown = (
    e: SyntheticEvent<Element, Event>,
    newValue: string | number,
  ): void => {
    Log.debug("Dropdown bound template value changed to:", newValue);
    setValue(name, newValue, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <StationDropdownBoundTemplateWrapper $width={calculatedWidth}>
      <div className="inner-container">
        <Controller
          control={control}
          name={name}
          render={({
            field: { value: formValue, onChange, ...restField },
            fieldState: { error },
          }) => {
            return (
              <FormControl
                data-e2e-field-name={name}
                required={requiredComputed}
                fullWidth
                error={!!error}
              >
                <Autocomplete
                  {...restField}
                  size={size}
                  data-e2e={e2eData}
                  data-e2e-id={`${name}_${id || ""}`}
                  disabled={isDisabled}
                  options={dropdownOptions || []}
                  value={formValue ?? null}
                  onChange={(event, newValue) => {
                    checkCleanupNestedValidation(name, errors);
                    onChangeDropdown(event, newValue as string | number);
                    onSelect?.(dropdownItems?.find(e => e.value === newValue));
                  }}
                  isOptionEqualToValue={(opt, val) =>
                    String(opt) === String(val)
                  }
                  getOptionLabel={getOptionLabel}
                  renderInput={params => (
                    <TextField
                      {...params}
                      error={!!error}
                      label={
                        isDisabledByMissingAncestor
                          ? disabledFormLabelTranslated
                          : formLabelTranslated
                      }
                      data-e2e-template="station-dropdown-bound-template"
                      disabled={isDisabled}
                      required={requiredComputed}
                    />
                  )}
                />
                {error && (
                  <FormHelperText data-e2e="station-dropdown-bound-template-error">
                    {error.message}
                  </FormHelperText>
                )}
              </FormControl>
            );
          }}
        />

        <div className="add-button-container">
          {isAddButtonVisible && (
            <AccessControl
              entity={dropItemsSource || "unknown"}
              action="create"
            >
              <FramerTooltip
                content={t("general.ADD_NEW_DROPDOWN_ITEM_TOOLTIP")}
                placement={PopoverPlacement.top}
              >
                <IconButton onClick={handleInsertItem}>
                  <AddCircleOutlined />
                </IconButton>
              </FramerTooltip>
            </AccessControl>
          )}
        </div>
        <div className="find-nearby-button-container">
          {formAttributes?.isNearbyButtonVisible && (
            <AccessControl
              entity={dropItemsSource || "unknown"}
              action="create"
            >
              <FramerTooltip
                content={t("general.NEARBY_STATION.FIND")}
                placement={PopoverPlacement.top}
              >
                <IconButton onClick={handleFindNearbyStation}>
                  <SVG_MUI.NearbyLocation2SVG />
                </IconButton>
              </FramerTooltip>
            </AccessControl>
          )}
        </div>
      </div>
    </StationDropdownBoundTemplateWrapper>
  );
};
